import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiBriefcase } from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';

const RecruiterLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    organization: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      const response = await apiFetch('/auth/google/url', {
        method: 'GET'
      });
      
      if (response.auth_url) {
        // Add recruiter flag to state parameter
        const url = new URL(response.auth_url);
        url.searchParams.set('state', 'recruiter_signup');
        
        // Store intent in localStorage for callback handling
        localStorage.setItem('auth_intent', 'recruiter');
        
        window.location.href = url.toString();
      } else {
        setError('Failed to initialize Google authentication');
      }
    } catch (err) {
      setError('Google authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if already logged in as recruiter
    const token = localStorage.getItem('recruiter_token');
    if (token) {
      navigate('/recruiter');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };





  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isLogin ? '/recruiter/login' : '/recruiter/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : { 
            name: formData.name, 
            email: formData.email, 
            password: formData.password, 
            org: formData.organization 
          };

      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      localStorage.setItem('recruiter_token', response.access_token);
      navigate('/recruiter');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      gap: '20px',
      flexWrap: 'wrap'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '40px',
          width: '100%',
          maxWidth: '450px',
          flex: '0 1 450px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #4f8cff, #6366f1)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white',
              fontSize: '32px'
            }}
          >
            <FiBriefcase />
          </motion.div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1a202c',
            margin: '0 0 8px'
          }}>
            {isLogin ? 'Welcome Back' : 'Join Our Platform'}
          </h1>
          <p style={{
            color: '#718096',
            fontSize: '16px',
            margin: 0
          }}>
            {isLogin ? 'Sign in to your recruiter account' : 'Create your recruiter account'}
          </p>
        </div>

        {/* Toggle Buttons */}
        <div style={{
          display: 'flex',
          background: '#f7fafc',
          borderRadius: '12px',
          padding: '4px',
          marginBottom: '30px'
        }}>
          <button
            type="button"
            onClick={() => setIsLogin(true)}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderRadius: '8px',
              background: isLogin ? '#4f8cff' : 'transparent',
              color: isLogin ? 'white' : '#718096',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setIsLogin(false)}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderRadius: '8px',
              background: !isLogin ? '#4f8cff' : 'transparent',
              color: !isLogin ? 'white' : '#718096',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Sign Up
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            style={{
              background: '#fed7d7',
              color: '#c53030',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              border: '1px solid #feb2b2'
            }}
          >
            {error}
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              style={{ marginBottom: '20px' }}
            >
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#2d3748',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Full Name
              </label>
              <div style={{ position: 'relative' }}>
                <FiUser style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#a0aec0',
                  fontSize: '18px'
                }} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required={!isLogin}
                  placeholder="Enter your full name"
                  style={{
                    width: '100%',
                    padding: '14px 16px 14px 48px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4f8cff'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </motion.div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#2d3748',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <FiMail style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#a0aec0',
                fontSize: '18px'
              }} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email"
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 48px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4f8cff'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>

          {!isLogin && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              style={{ marginBottom: '20px' }}
            >
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#2d3748',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Organization
              </label>
              <div style={{ position: 'relative' }}>
                <FiBriefcase style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#a0aec0',
                  fontSize: '18px'
                }} />
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  placeholder="Enter your organization"
                  style={{
                    width: '100%',
                    padding: '14px 16px 14px 48px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4f8cff'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </motion.div>
          )}

          <div style={{ marginBottom: '30px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#2d3748',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <FiLock style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#a0aec0',
                fontSize: '18px'
              }} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '14px 48px 14px 48px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4f8cff'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#a0aec0',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: loading ? '#a0aec0' : 'linear-gradient(135deg, #4f8cff, #6366f1)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(79, 140, 255, 0.3)'
            }}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Processing...
              </div>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </motion.button>
        </form>



        {/* Convert to Recruiter */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ color: '#718096', fontSize: '14px', marginBottom: '10px' }}>Already have a student account?</p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#4f8cff',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline',
              fontWeight: '600'
            }}
          >
            Switch to Student Login
          </button>
        </div>

        {/* Google OAuth */}
        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            margin: '20px 0'
          }}>
            <div style={{
              flex: 1,
              height: '1px',
              background: '#e2e8f0'
            }} />
            <span style={{
              padding: '0 16px',
              color: '#718096',
              fontSize: '14px'
            }}>
              or
            </span>
            <div style={{
              flex: 1,
              height: '1px',
              background: '#e2e8f0'
            }} />
          </div>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGoogleLogin}
            disabled={loading}
            type="button"
            style={{
              width: '100%',
              padding: '14px',
              background: 'white',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              color: '#2d3748',
              transition: 'all 0.3s ease'
            }}
          >
            <FaGoogle style={{ color: '#4285f4', fontSize: '20px' }} />
            Continue with Google (Recruiter)
          </motion.button>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                background: 'none',
                border: 'none',
                color: '#4f8cff',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              ← Back to Home
            </button>
            <span style={{ color: '#e2e8f0' }}>|</span>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                background: 'none',
                border: 'none',
                color: '#4f8cff',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              Student Login
            </button>
          </div>
          <p style={{
            margin: '16px 0 0',
            fontSize: '12px',
            color: '#a0aec0',
            lineHeight: '1.4'
          }}>
            Secure recruiter portal powered by AI-driven candidate matching
          </p>
        </div>
      </motion.div>

      {/* Side Notice Box */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '30px',
          width: '100%',
          maxWidth: '400px',
          flex: '0 1 400px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          border: '2px solid #667eea'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
          }}>⚡</div>
          <h3 style={{ margin: 0, color: '#667eea', fontSize: '17px', fontWeight: '700' }}>
            Google AI Recruiter Features
          </h3>
        </div>
        
        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
          <strong>No Domain Purchased:</strong> Google only allows whitelisted accounts to sign in. Without a verified domain, only pre-authorized emails can use Google OAuth.
        </p>
        
        <div style={{ background: '#f8f9ff', padding: '14px', borderRadius: '10px', marginBottom: '16px' }}>
          <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600', color: '#333' }}>✨ AI-Powered Suite:</p>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', lineHeight: '1.9', color: '#666' }}>
            <li>Gmail - Auto-parse resumes</li>
            <li>Calendar - Schedule interviews</li>
            <li>Google Meet - Instant links</li>
            <li>Drive - Organize documents</li>
            <li>Notifications - Real-time updates</li>
          </ul>
        </div>
        
        <div style={{ background: '#fff3cd', padding: '12px', borderRadius: '8px', marginBottom: '14px', borderLeft: '4px solid #ffc107' }}>
          <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#856404', fontWeight: '600' }}>
            📧 <a href="mailto:abhishetty1506@gmail.com" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '700', borderBottom: '2px solid #667eea' }}>abhishetty1506@gmail.com</a>
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#856404', fontWeight: '600' }}>
            📱 <a href="tel:+919030633137" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '700', borderBottom: '2px solid #667eea' }}>+91 90306 33137</a>
          </p>
        </div>
        
        <p style={{ margin: 0, fontSize: '12px', color: '#28a745', fontWeight: '600' }}>
          ✅ Send your email to get instant access to all Google AI features!
        </p>
      </motion.div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 950px) {
          /* Stack vertically on smaller screens */
        }
      `}</style>
    </div>
  );
};

export default RecruiterLogin;